import { ActionContext, ActionTree } from 'vuex'
import {
  IChallengePayload,
  IProductExtended,
  IRootState,
  ISubscriptionPayload,
  ISubscriptionsState,
} from '@/@types'
import {
  fetchCouponInfo,
  fetchPaymentInfo,
  fetchSubscriptions,
  postCheckout,
} from '@/models/subscriptions'
import { getAccessToken, setAccessToken } from '@internetworkexpert/js-common'

import CONSTANTS from '@/constants'
import { eventsConstants } from '@/helpers/eventsList'
import router from '@/router'

const { PLANS, BLACK_FRIDAY_PRODUCT_BUNDLES, LEAP_YEAR_PROMO_PRODUCT_BUNDLES } = CONSTANTS
const planCodes = Object.values(PLANS)

interface IErrorNewUser extends Error {
  error: Error
  new_user?: {
    access_token: string
  }
  processor_error?: string
  processor_error_code?: string
  actionTokenId?: string
}

const actions: ActionTree<ISubscriptionsState, IRootState> = {
  submitPaymentInfo: async (
    context: ActionContext<ISubscriptionsState, IRootState>,
    params: {
      challengeToken: string | null
      captchaToken: string | null
    }
  ) => {
    const { challengeToken, captchaToken } = params
    const selectedPlan = context.rootGetters['shoppingCart/selectedPlan']
    const selectedSkillDivePlan = context.rootGetters['shoppingCart/selectedSkillDivePlan']
    const user_data = context.rootState.auth.userProfile ?? ''
    const coupon = context.rootState.shoppingCart?.coupon ?? ''
    const billing_token = context.state?.recurlyToken ?? ''
    const accessToken = getAccessToken()
    const filteredItems = context.rootState.shoppingCart.shoppingCart.filter(
      i => i && i.merchant_id && !planCodes.includes(i.merchant_id)
    )
    const isPromotionActive = context.rootGetters['shoppingCart/getIsPromotionActive']
    const promotionItemsCodes = [
      ...Object.values(BLACK_FRIDAY_PRODUCT_BUNDLES),
      ...Object.values(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
    ]
    let promoBundle: IProductExtended
    if (isPromotionActive) {
      promoBundle = context.rootState.shoppingCart.shoppingCart.find(i =>
        promotionItemsCodes.includes(i.merchant_id!)
      )!
    }
    const items =
      filteredItems && filteredItems.length
        ? filteredItems.map(i => {
            return { code: i.merchant_id }
          })
        : []
    const utm = context.rootState.events.utmParams ?? {}
    const plans = []
    if (selectedPlan.merchant_id) {
      const plan = {
        code: selectedPlan.merchant_id,
        trial: selectedPlan.plan_trial ?? false,
        add_ons: [],
      }
      plans.push(plan)
    }
    if (selectedSkillDivePlan.merchant_id) {
      const plan = {
        code: selectedSkillDivePlan.merchant_id,
        trial: false,
        add_ons: selectedSkillDivePlan.addOnsOnCart.map(
          (SDAddOn: { merchant_id: any }) => SDAddOn.merchant_id
        ),
      }
      plans.push(plan)
    }

    const payload = {
      plans,
      coupon,
      billing_token,
      origin: process.env.VUE_APP_ORIGIN !== undefined ? process.env.VUE_APP_ORIGIN : '',
      items,
      utm,
      captchaToken,
    }
    if (isPromotionActive && promoBundle!) {
      const newItemsObj = items.filter(i => !promotionItemsCodes.includes(i.code))
      if (promoBundle.merchant_id === BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_THREE) {
        const fundamentals = context.rootState.products.availablePlans.find(
          item => item.merchant_id && item.merchant_id === PLANS.ANNUAL
        )
        const fundamentalsPlan = {
          code: fundamentals!.merchant_id,
          trial: fundamentals!.plan_trial ?? false,
          add_ons: [],
        }
        Object.assign(payload, { plans: [fundamentalsPlan], items: newItemsObj })
      } else {
        Object.assign(payload, { bundle: promoBundle!.bundle, items: newItemsObj })
      }
    }
    try {
      if (accessToken) {
        const payloadWAccessToken: IChallengePayload = Object.assign({}, payload, {
          payment_challenge_token: challengeToken ?? '',
          accessToken,
        })
        return await postCheckout(payloadWAccessToken)
      }
      const payloadWUserData: ISubscriptionPayload = Object.assign({}, payload, {
        user_data,
      })
      const returnedResponse = await postCheckout(payloadWUserData)
      const { access_token, user_id } = returnedResponse
      setAccessToken(access_token)
      return returnedResponse
    } catch (errorObject) {
      const error = errorObject as IErrorNewUser
      let attemptResponse
      const { processor_error_code } = error
      const isNewUser = error.new_user
      if (isNewUser) {
        const new_user_access_token = isNewUser.access_token
        setAccessToken(new_user_access_token)
        attemptResponse = await context.dispatch('auth/attemptLogin', new_user_access_token, {
          root: true,
        })
        await context.dispatch(
          'events/sendMixpanelEvent',
          { event_name: eventsConstants.CHECKOUT_USER_SIGNUP },
          {
            root: true,
          }
        )
        attemptResponse.user_id = attemptResponse?.id
      }
      const { message } = error.error ? error.error : (error as Error)
      if (message === 'The email has already been taken.') {
        router.push({
          name: 'account',
          params: { action: 'login' },
        })
        return context.dispatch(
          'app/setError',
          'Payment declined. The email has already been taken.',
          {
            root: true,
          }
        )
      }
      context.commit('THREE_DS_FLOW_UPDATED', false)
      if (processor_error_code && processor_error_code.length) {
        if (processor_error_code === 'three_d_secure_action_required') {
          const { actionTokenId } = error
          const td_access_token = getAccessToken()
          return context.dispatch('trigger3dsFlow', {
            actionTokenId,
            td_access_token,
          })
        }
        if (processor_error_code === 'declined') {
          context.dispatch(
            'app/setError',
            `Your charge has been declined. Please contact your bank if you believe this was a mistake.`,
            {
              root: true,
            }
          )
          if (isNewUser) {
            attemptResponse.error = true
            return attemptResponse
          }
          return false
        }
        context.dispatch(
          'app/setError',
          'There was an issue with the credit card provided. Please contact your bank.',
          {
            root: true,
          }
        )
        if (isNewUser) {
          attemptResponse.error = true
          return attemptResponse
        }
        return false
      }
      if (
        message === 'Token is either invalid or expired' ||
        message === "Trying to get property 'account_code' of non-object"
      ) {
        context.commit('RECURLY_TOKEN_UPDATED', '')
        router.push({ name: 'payment' })
        return context.dispatch(
          'app/setError',
          'Session expired. Please re-enter your payment details.',
          {
            root: true,
          }
        )
      }
      if (isPromotionActive && promoBundle!) {
        if (
          message ===
          'The plans field is required when none of items / bundle are present., The items field is required when none of plans / bundle are present.'
        ) {
          return context.dispatch(
            'app/setError',
            'The INE Security Certification field is required. Please choose a certification.',
            {
              root: true,
            }
          )
        }
      }
      context.dispatch('app/setError', message, { root: true })
    } finally {
      context.dispatch('app/setIsComponentLoading', false, { root: true })
    }
  },
  setPartnerStackFlow: (
    context: ActionContext<ISubscriptionsState, IRootState>,
    payload: boolean
  ) => {
    context.commit('PARTNER_STACK_FLOW_UPDATED', payload)
  },
  trigger3dsFlow: (context: ActionContext<ISubscriptionsState, IRootState>, { actionTokenId }) => {
    context.commit('RECURLY_ACTION_TOKEN_ID_UPDATED', actionTokenId)
    context.commit('THREE_DS_FLOW_UPDATED', true)
  },
  resolve3dsFlow: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.dispatch('app/setIsComponentLoading', true, { root: true })
    context.commit('RECURLY_ACTION_TOKEN_RESULT_UPDATED', payload)
    context.commit('THREE_DS_FLOW_UPDATED', false)
  },
  getCurrentPaymentMethodOnFile: async (
    context: ActionContext<ISubscriptionsState, IRootState>
  ) => {
    try {
      const method = await fetchPaymentInfo()
      context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', method)
      return method
    } catch (error) {
      context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', '')
    }
  },
  getSubscriptionsWithPasses: async (context: ActionContext<ISubscriptionsState, IRootState>) => {
    try {
      const response = await fetchSubscriptions()
      context.commit('USER_SUBSCRIPTIONS_WITH_PASSES_UPDATED', response)
      return response
    } catch (error) {
      context.commit('USER_SUBSCRIPTIONS_WITH_PASSES_UPDATED', '')
    }
  },
  getCouponInformation: async (
    context: ActionContext<ISubscriptionsState, IRootState>,
    couponCode: string
  ) => {
    try {
      const couponInformation = await fetchCouponInfo(couponCode)
      context.commit('COUPON_INFORMATION_UPDATED', couponInformation)
      return couponInformation
    } catch (error) {
      context.commit('COUPON_INFORMATION_UPDATED', error)
    }
  },
  removePaymentMethodOnFile: async (context: ActionContext<ISubscriptionsState, IRootState>) => {
    context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', '')
  },
  updatePaymentEntries: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PAYMENT_ENTRIES_UPDATED', payload)
  },
  updateCheckoutDiscount: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_DISCOUNT_UPDATED', payload)
  },
  updateCheckoutCouponDiscount: (
    context: ActionContext<ISubscriptionsState, IRootState>,
    payload
  ) => {
    context.commit('CHECKOUT_COUPON_DISCOUNT_UPDATED', payload)
  },
  updateRecurlyPricing: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('RECURLY_PRICING_UPDATED', payload)
  },
  updateCheckoutTaxes: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_TAXES_UPDATED', payload)
  },
  updateCheckoutSubtotal: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_SUBTOTAL_UPDATED', payload)
  },
  updateCheckoutTotal: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('CHECKOUT_TOTAL_UPDATED', payload)
  },
  setRecurlyToken: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('RECURLY_TOKEN_UPDATED', payload)
  },
  setPaymentMethod: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PAYMENT_METHOD_UPDATED', payload)
  },
  setPromotionalField: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PROMOTIONAL_FIELD_UPDATED', payload)
  },
}

export default actions
